import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import type { Patient } from "$global-providers/rxdb-provider/collections/patient";
import { useUser } from "$global-providers/user-provider";
import { InsuranceTab } from "$routes/patient/[:patientId]/_lib/insurance-tab";
import { VaccinationsSchedule } from "$routes/patient/[:patientId]/_lib/vaccination-schedule-table";
import { useVaccinationsService } from "$routes/patient/[:patientId]/_lib/vaccinations-service/provider";
import { History } from "$routes/patient/[:patientId]/overview/history/_lib/history";
import { styled, Tab, Tabs } from "@material-ui/core";
import { Role } from "elephant-web/gql";
import React, { useState } from "react";
import { useIntl } from "react-intl";

function useInitialTab(): TabOption {
  const { role } = useUser();
  const { getIsChildPatient } = useVaccinationsService();
  const [vaccineScheduleFlag] = useFeatureFlag(
    featureFlags.immunisations.schedule,
  );
  const isChildPatient = getIsChildPatient();
  const isBillingUser = [Role.CashOfficer, Role.Clinic].includes(role);
  return isBillingUser
    ? TabOption.History
    : vaccineScheduleFlag && isChildPatient
      ? TabOption.Immunisations
      : TabOption.Insurance;
}

const FlexColumn = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const PatientHistoryAndInsuranceSidebar: FC<
  {
    patient: Patient;
  } & React.ComponentProps<"div">
> = ({ patient, ...restProps }) => {
  const initialTab = useInitialTab();
  const [tabValue, setTabValue] = useState(initialTab);

  return (
    <FlexColumn {...restProps}>
      <FlexColumn>
        <TabButtons setTab={setTabValue} tab={tabValue} />
        {tabValue === TabOption.History ? (
          <History patient={patient} isSideBar={true} />
        ) : tabValue === TabOption.Insurance ? (
          <InsuranceTab uuid={patient.id} />
        ) : (
          <VaccinationsSchedule />
        )}
      </FlexColumn>
    </FlexColumn>
  );
};

enum TabOption {
  History = 0,
  Insurance = 1,
  Immunisations = 2,
}

const TabButtons: FC<{
  tab: TabOption;
  setTab: (option: TabOption) => void;
}> = ({ tab, setTab }) => {
  const { getIsChildPatient } = useVaccinationsService();
  const { formatMessage } = useIntl();
  const [vaccineScheduleFlag] = useFeatureFlag(
    featureFlags.immunisations.schedule,
  );
  const isChildPatient = getIsChildPatient();
  function a11yProps(givenTab: TabOption) {
    return {
      id: `billing-sidebar-tab-${givenTab}`,
      "aria-controls": `billing-sidebar-tab-${givenTab}`,
    };
  }

  return (
    <Tabs
      onChange={(_, value) => {
        setTab(value);
      }}
      value={tab}
      variant="fullWidth"
    >
      {vaccineScheduleFlag && isChildPatient && (
        <Tab
          label={formatMessage({
            id: "billingsidebar.tabs.vaccinationsLabel",
            defaultMessage: "Vaccinations",
          })}
          value={TabOption.Immunisations}
          {...a11yProps(TabOption.Immunisations)}
          data-testid="billingSidebar.immunisations.labelTab"
        />
      )}
      <Tab
        label={formatMessage({
          id: "billingsidebar.tabs.medicalHistoryLabel",
          defaultMessage: "History",
        })}
        value={TabOption.History}
        {...a11yProps(TabOption.History)}
        data-testid="billingSidebar.medicalHistory.labelTab"
      />
      <Tab
        label={formatMessage({
          id: "billingsidebar.tabs.insuranceLabel",
          defaultMessage: "Insurance",
        })}
        value={TabOption.Insurance}
        {...a11yProps(TabOption.Insurance)}
        data-testid="billingSidebar.insurance.labelTab"
      />
    </Tabs>
  );
};
